import i18n, { i18n_t } from '@/locales/i18n';
import EventEmitter from '@/common/EventEmitter';
import { User } from '@/renderer/scripts/utils/user';
import ConfigConfirmer from '@/renderer/scripts/CoSync/SyncCenter/ConfigConfirmer';
import { CoSyncCenterEvent } from '@/renderer/scripts/CoSync/SyncCenter/CoSyncCenterEvent';
import SyncGlobalRepo from '@/renderer/scripts/CoSync/SyncCenter/Repo/SyncGlobalRepo';
import SyncServiceProxy from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/Service/SyncServiceProxy';
import {
  CmdFromRenderer,
  CmdTypeFromRender,
  StartCmdparam,
} from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromRenderer';
import { CmdFromWorker } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromWorker';
import { CoSynStatusInfo } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncEvent';
import { CoSyncInfo, CoSyncTaskConfig } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import { syncCenterEventBus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenterEventBus';
import { SyncCenterDataRelay } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenterDataRelay';
import { QuerySyncInfoData } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import { checkSysSyncEntry } from '@/renderer/scripts/utils/syncTaskLinkHelper';
import tips from '@/renderer/scripts/utils/tips';
import ipc, { IpcEvents } from '../../utils/ipc';
import { MigrationMgr } from './Migrate/MigrationMgr';

export interface SyncTaskUIStatus {
  coSyncInfo: CoSyncInfo;
  statusInfo: CoSynStatusInfo;
}

export interface CmdFromSyncTask {
  coSyncInfo: CoSyncInfo;
  cmd: CmdFromWorker;
}

// eslint-disable-next-line no-use-before-define
const waitUntilReady = function waitUntilReady(target: SyncCenter, name: PropertyKey, descriptor: PropertyDescriptor) {
  const fn = descriptor.value;
  descriptor.value = target.wrapReady(fn);
};

/**
 * @description 同步盘的中枢，管理和操作所有同步盘任务
 * @export
 * @class SyncCenter
 */
export default class SyncCenter {
  private readonly userInfo: CoFile.UserInfo;
  private readonly publicDataRelay: SyncCenterDataRelay;

  ready: Promise<SyncCenter>;

  private serviceProxyMap: Map<number, SyncServiceProxy>;

  private syncGlobalRepo: SyncGlobalRepo;
  private configConfirmer: ConfigConfirmer;

  private queryConfigListTimeout: NodeJS.Timeout | null = null;

  private migrationMgr: MigrationMgr;

  constructor(userInfo: CoFile.UserInfo, dataRelay: SyncCenterDataRelay) {
    this.userInfo = userInfo;
    this.publicDataRelay = dataRelay; // 提供给外部访问的数据

    this.serviceProxyMap = new Map<number, SyncServiceProxy>();

    this.syncGlobalRepo = new SyncGlobalRepo(userInfo);
    this.configConfirmer = new ConfigConfirmer(this.syncGlobalRepo);
    this.migrationMgr = new MigrationMgr(new WeakRef(this), this.syncGlobalRepo, this.userInfo);

    this.ready = new Promise<SyncCenter>(async (resolve, reject) => {
      let allConfigList: CoSyncInfo[] = [];
      try {
        logger.ui.info('SyncCenter ready, start getAllConfig', allConfigList);
        allConfigList = await this.syncGlobalRepo.getAllConfig();
        logger.ui.info('SyncCenter ready, start getAllConfig done', allConfigList);
      } catch (err) {
        logger.ui.error('SyncCenter ready, getAllConfig error', err);
        tips.error(i18n_t('同步服务初始化失败，请重启软件'));
      }

      if (allConfigList.length === 0) {
        this.publicDataRelay.setupEmptySyncTaskStatusMap();
        syncCenterEventBus.emit(CoSyncCenterEvent.onTaskStatusMapChange, this.publicDataRelay.syncTaskStatusMap);
      } else {
        for (const config of allConfigList) {
          this.addServiceImpl(userInfo, config, false);
        }
      }

      checkSysSyncEntry(allConfigList.length === 0);

      // ready之后3秒查询后台，更新本地的同步盘信息（路径）
      if (this.queryConfigListTimeout) {
        clearTimeout(this.queryConfigListTimeout);
      }
      this.queryConfigListTimeout = setTimeout(() => {
        this.queryUpdateConfigList();
      }, 3000);

      resolve(this);

      this.migrationMgr.migrateIfNeed();
    });
  }

  // eslint-disable-next-line class-methods-use-this
  public wrapReady(fn: Function) {
    return function wrapReady(...args: any[]): Promise<any> {
      // @ts-ignore
      return this.ready.then(() => fn.apply(this, args));
    };
  }

  /**
   * @description 将一些数据清理重置
   * @return {*}  {Promise<void>}
   * @memberof SyncCenter
   */
  @waitUntilReady
  public async unInit(): Promise<void> {
    if (this.queryConfigListTimeout) {
      clearTimeout(this.queryConfigListTimeout);
    }
    const serviceIdList = [...this.serviceProxyMap.keys()];
    for (const serviceId of serviceIdList) {
      const isPermanent = false;
      await this.removeService(serviceId, isPermanent);
    }
  }

  /**
   * @description 增加同步服务
   * @param {CoSyncTaskConfig} taskConfig
   * @return {*}  {Promise<number>}
   * @memberof SyncCenter
   */
  @waitUntilReady
  public async addService(taskConfig: CoSyncTaskConfig): Promise<CoSyncInfo> {
    logger.ui.info('SyncCenter addService', taskConfig);

    const remoteSyncId = await this.configConfirmer.checkIfAppendable(taskConfig);
    const coSyncInfo = this.syncGlobalRepo.addConfig(taskConfig, remoteSyncId);
    logger.ui.info('SyncCenter addService', remoteSyncId, coSyncInfo.localSyncId);
    const isJustCreated = taskConfig.syncSetting.keepWhichLastStatus === 'started';
    this.addServiceImpl(this.userInfo, coSyncInfo, isJustCreated);
    return coSyncInfo;
  }

  /**
   * @description 初始化同步服务
   * @private
   * @param {CoFile.UserInfo} userInfo
   * @param {CoSyncInfo} coSyncInfo
   * @memberof SyncCenter
   */
  private addServiceImpl(userInfo: CoFile.UserInfo, coSyncInfo: CoSyncInfo, isJustCreated: boolean) {
    const serviceProxy = new SyncServiceProxy(userInfo, coSyncInfo, this.syncGlobalRepo);
    this.serviceProxyMap.set(coSyncInfo.localSyncId, serviceProxy);

    // 订阅回调
    serviceProxy
      .on(CoSyncCenterEvent.onWorkerCmd, (params?: any) => {
        const cmd = params as CmdFromSyncTask;
        syncCenterEventBus.emit(CoSyncCenterEvent.onWorkerCmd, cmd);
      })
      .on(CoSyncCenterEvent.onTaskStatusChange, (params?: any) => {
        const status = params as SyncTaskUIStatus;
        this.publicDataRelay.setSyncTaskStatusMap(status.coSyncInfo.localSyncId, status);

        syncCenterEventBus.emit(CoSyncCenterEvent.onTaskStatusChange, status);
        syncCenterEventBus.emit(CoSyncCenterEvent.onTaskStatusMapChange, this.publicDataRelay.syncTaskStatusMap);
      });

    if (isJustCreated) {
      serviceProxy.handleJustCreated();
    } else {
      serviceProxy.handleRestore();
    }
  }

  /**
   * @description 移除同步服务
   * @param {number} serviceId
   * @param {boolean} [isPermanent=true]
   * @return {*}  {Promise<void>}
   * @memberof SyncCenter
   */
  @waitUntilReady
  public async removeService(serviceId: number, isPermanent = true): Promise<void> {
    logger.ui.info('SyncCenter removeService', serviceId, isPermanent);
    const serviceProxy = this.serviceProxyMap.get(serviceId);
    if (!serviceProxy) {
      logger.ui.error('SyncCenter removeService unknwon serviceId', serviceId);
      return Promise.reject({ code: 'UnknwonLocalSyncId' });
    }

    serviceProxy.off(CoSyncCenterEvent.onWorkerCmd, '*').off(CoSyncCenterEvent.onTaskStatusChange, '*');
    this.serviceProxyMap.delete(serviceId);
    this.publicDataRelay.deleteSyncTaskStatusMap(serviceId);
    syncCenterEventBus.emit(CoSyncCenterEvent.onTaskRemove, { serviceId });
    syncCenterEventBus.emit(CoSyncCenterEvent.onTaskStatusMapChange, this.publicDataRelay.syncTaskStatusMap);

    // 通知完UI更新后再进行异步任务，避免异步任务出错后UI异常
    await this.syncGlobalRepo.removeConfig(serviceId, isPermanent);
    await serviceProxy.destory(isPermanent);
  }

  /**
   * @description 让某个同步服务执行命令
   * @param {number} syncServiceId
   * @param {CmdFromRenderer} command
   * @return {*}  {Promise<any>}
   * @memberof SyncCenter
   */
  @waitUntilReady
  public async applyCommand(syncServiceId: number, command: CmdFromRenderer): Promise<any> {
    const serviceProxy = this.serviceProxyMap.get(syncServiceId);
    if (!serviceProxy) {
      logger.ui.error('SyncCenter applyCommand unknwon serviceId', syncServiceId);
      return Promise.reject({ code: 'UnknwonLocalSyncId' });
    }

    return serviceProxy.applyCommand(command);
  }

  public async queryUpdateConfigList() {
    const coSyncInfoList = Array.from(this.serviceProxyMap.values()).map(it => it.getCoSyncInfo());
    const remoteSyncId2LocalSyncId = new Map(coSyncInfoList.map(it => [it.syncId, it.localSyncId]));

    let queryDataList: QuerySyncInfoData[] = [];

    try {
      queryDataList = await this.configConfirmer.queryRemoteSyncInfo(coSyncInfoList);
    } catch (error) {
      logger.ui.warn('queryRemoteSyncInfo failed', error);
      return;
    }

    if (queryDataList.length <= 0) return;

    queryDataList.forEach(data => {
      const localSyncId = remoteSyncId2LocalSyncId.get(data.syncId);
      if (!localSyncId) {
        logger.ui.warn('server return unknwon syncid', data.syncId);
        return;
      }

      const servceProxy = this.serviceProxyMap.get(localSyncId);
      if (!servceProxy) {
        // 在请求的过程中，已经被删掉了（可以是ui删除）
        return;
      }

      servceProxy.updateCoSyncInfo(data);
    });
  }

  @waitUntilReady
  public async haveSyncTask(): Promise<boolean> {
    return this.serviceProxyMap.size > 0;
  }
}
